import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Grid, Paper, Typography } from '@mui/material';
import { SiTrackPage } from '../../components/si-track-page';
import { SelectResult } from '../../components/select/select-chain.component';

import { SiTrackGrid } from '../../components/si-track-grid/si-track-grid';
import { workorderReportApiKeyStore } from '../../api/workorder-report-api-key-store';
import { useLoadData } from '../../hooks/useLoadData';
import { ApiResponse } from '../../models/api-response';
import { workOderInspectionReportColumns } from './work-order-inspection-report-columns';
import { ReactHookForm } from '../../components/hook-form/react-hook-form.component';
import { useReactHookForm } from '../../hooks/useReactHookForm';
import { WorkOrderInspectionFilters } from './work-order-inspection-report.filters';
import { UserPermissionName } from '../../models/user-permission-name';
import React from 'react';
import { GridRowProps } from '@progress/kendo-react-grid';
import { GridColumnConfig, GridPreference } from '../../models/gridCoumnConfig';
import { gridApiKeyStore } from '../../api/grid-api-key-store';
import { useSaveData } from '../../hooks/useSaveData';
import { State } from '@progress/kendo-data-query';
import useDebounce from '../../hooks/use-debounce';
import { WorkOrderInspectionReport } from '../../models/work-order-inspection-report';

const styling = {
  paperStyle: { padding: '20px', borderRadius: '10px' },
};

interface CustomGridCellProps {
  field: string;
}

interface CellProps {
  workorder: any;
}

const getInspectorWithFilters = (filter: any) => {
  const { customerID, officeID, projectID, unitID, workOrderID, serviceID, wOStatusID } = filter;

  let queryString = '';

  if (customerID) {
    queryString += `${queryString ? '&' : ''}customerID=${customerID}`;
  }
  if (officeID) {
    queryString += `${queryString ? '&' : ''}officeID=${officeID}`;
  }
  if (projectID) {
    queryString += `${queryString ? '&' : ''}projectID=${projectID}`;
  }
  if (unitID) {
    queryString += `${queryString ? '&' : ''}unitID=${unitID}`;
  }
  if (serviceID) {
    queryString += `${queryString ? '&' : ''}service=${serviceID}`;
  }
  if (wOStatusID) {
    queryString += `${queryString ? '&' : ''}workOrderStatusID=${wOStatusID}`;
  }
  if (workOrderID) {
    queryString += `${queryString ? '&' : ''}workOrderID=${workOrderID}`;
  }
  return queryString;
};

const getFiltesFromUrl = (params: URLSearchParams) => {
  return {
    customerID: params.get('customerID'),
    officeID: params.get('officeID'),
    projectID: params.get('projectID'),
    unitID: params.get('unitID'),
  };
};

export const WorkOrderInspectionReportPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedFilters, setSelectedFilters] = useState<SelectResult>(getFiltesFromUrl(searchParams));

  const form = useReactHookForm({
    defaultValues: {
      customer: { customerID: '', customerName: '' },
      office: { officeID: '', officeName: '' },
      project: { projectID: '', projectName: '' },
      unit: { unitID: '', unitName: '' },
      user: { userID: '', fullName: '' },
      service: { serviceID: '', serviceName: '' },
      dateCriteria: { dateCriteriaValue: '', dateCriteria: '' },
      workOrderStatus: { workOrderStatusID: '', workOrderStatus: '' },
      projectStatus: { statusValue: 'active', statusLabel: 'Active' },
      action: '',
    },
  });

  const { data, isLoading, isFetching } = useLoadData<ApiResponse<any[]>>(
    workorderReportApiKeyStore.getWorkOrderInspectionReports(selectedFilters),
    !!selectedFilters?.customerID ||
    !!selectedFilters?.officeID ||
    !!selectedFilters?.projectID ||
    !!selectedFilters?.unitID ||
    !!selectedFilters?.serviceID ||
    !!selectedFilters?.inspectorID ||
    !!selectedFilters?.wOStatusID ||
    !!selectedFilters?.dateFrom ||
    !!selectedFilters?.dateTo ||
    !!selectedFilters?.dateCriteria ||
    !!selectedFilters?.projectStatus ||
    !!selectedFilters?.showAll
  );

  const handleSubmit = (filtersData: any) => {
    const filteringData = {
      showAll: filtersData?.action ?? '',
      customerID: filtersData?.customer?.customerID ?? 0,
      officeID: filtersData?.office?.officeID ?? 0,
      projectID: filtersData?.project?.projectID ?? 0,
      unitID: filtersData?.unit?.unitID ?? 0,
      serviceID: filtersData?.service?.serviceID ?? 0,
      inspectorID: filtersData?.user?.userID ?? 0,
      wOStatusID: filtersData?.workOrderStatus?.workOrderStatusID ?? 0,
      dateFrom: filtersData?.fromDate ?? '',
      dateTo: filtersData?.toDate ?? '',
      dateCriteria: filtersData?.dateCriteria?.dateCriteriaValue ?? '',
      projectStatus: filtersData?.projectStatus?.statusValue ?? '',
    };
    setSelectedFilters(filteringData);
  };

  const handleShowAll = () => {
    form.handleSubmit(handleSubmit)();
  };

  const schedulingRedirect = (workOrderID: any) => {
    selectedFilters.workOrderID = workOrderID;
    navigate(`/workorders?${getInspectorWithFilters(selectedFilters)}`);
  };

  const WorkOrderCell = ({ workorder }: CellProps) => {
    const [isHovered, setIsHovered] = useState(false);
    return (
      <td style={{ cursor: 'pointer' }} onClick={() => schedulingRedirect(workorder?.workOrderID)}>
        <Typography
          component="span"
          style={{
            color: isHovered ? '#0000b5' : '#0000b5',
            textDecoration: isHovered ? 'underline' : 'none',
            cursor: 'pointer',
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {workorder?.workOrderNumber}
        </Typography>
      </td>
    );
  };

  const InvoiceAmountCell = ({ workorder }: CellProps) => {
    const invoiceAmount = workorder?.invoiceAmountNumeric;

    const formattedAmount =
      invoiceAmount !== undefined && invoiceAmount !== null
        ? `$${parseFloat(invoiceAmount)?.toFixed(2)}` // Add dollar sign
        : '$0.00'; // Fallback to "0.00" if invoiceAmount is not available
    return (
      <td>
        <Typography component="span">{formattedAmount}</Typography>
      </td>
    );
  };

  const PriceAmountCell = ({ workorder }: CellProps) => {
    const priceAmount = workorder?.priceNumeric;

    const formattedAmount =
      priceAmount !== undefined && priceAmount !== null
        ? `$${parseFloat(priceAmount)?.toFixed(2)}` // Add dollar sign
        : '$0.00'; // Fallback to "0.00" if invoiceAmount is not available
    return (
      <td>
        <Typography component="span">{formattedAmount}</Typography>
      </td>
    );
  };

  const AccuralAmountCell = ({ workorder }: CellProps) => {
    const accrualAmount = workorder?.accrualAmountNumeric;

    const formattedAmount =
      accrualAmount !== undefined && accrualAmount !== null
        ? `$${parseFloat(accrualAmount)?.toFixed(2)}` // Add dollar sign
        : '$0.00'; // Fallback to "0.00" if invoiceAmount is not available
    return (
      <td>
        <Typography component="span">{formattedAmount}</Typography>
      </td>
    );
  };

  const JobsiteCell = ({ workorder }: CellProps) => {
    const [isHovered, setIsHovered] = useState(false);
    const handleClickJobsite = () => {
      // navigate(`/units?searchType=jobsites&searchText=${workorder?.jobsite}`);
      window.open(`/units/profile/${workorder?.unitID}`, "_blank");
    };
    return (
      <td style={{ cursor: 'pointer' }} onClick={handleClickJobsite}>
        <Typography
          component="span"
          style={{
            color: isHovered ? '#0000b5' : '#0000b5',
            textDecoration: isHovered ? 'underline' : 'none',
            cursor: 'pointer',
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {workorder?.jobsite}
        </Typography>
      </td>
    );
  };

  const CustomGridCell = ({ field }: CustomGridCellProps): ((dataItem: any) => JSX.Element) | undefined => {
    if (field === 'workOrderNumber') {
      return (dataItem: any) => {
        return <WorkOrderCell workorder={dataItem.dataItem} />;
      };
    }
    if (field === 'invoiceAmountNumeric') {
      return (dataItem: any) => {
        return <InvoiceAmountCell workorder={dataItem.dataItem} />;
      };
    }
    if (field === 'accrualAmountNumeric') {
      return (dataItem: any) => {
        return <AccuralAmountCell workorder={dataItem.dataItem} />;
      };
    }
    if (field === 'priceNumeric') {
      return (dataItem: any) => {
        return <PriceAmountCell workorder={dataItem.dataItem} />;
      };
    }
    if (field === 'jobsite') {
      // Return a function that takes a data item and returns a JSX element
      return (dataItem: any) => {
        return <JobsiteCell workorder={dataItem.dataItem} />;
      };
    }
    return undefined;
  };

  const rowRender = (row: React.ReactElement, rowProps: GridRowProps) => {
    const trElement = React.cloneElement(row, {
      style: { cursor: 'pointer' },
      onClick: () => handleRowClick(rowProps.dataItem as any),
    });
    return trElement;
  };

  const handleRowClick = (dataItem: any) => {
    schedulingRedirect(dataItem?.workOrderID);
  };

  // -------------------------------------------------Grid Layout------------------------------

  const {
    isLoading: isLoadingGridLayout,
    data: gridLayouts,
    refetch: refetchGridLayout
  } = useLoadData<ApiResponse<GridPreference>>(
    gridApiKeyStore.getGridLayout('WorkOrderInspectionReport', 'WorkOrderInspectionReport')
  );

  const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
  const [gridConfig, setGridConfig] = useState<{
    dataState: State | undefined;
    columns: any[] | undefined;
  }>();

  const [filteredWorkOrderInspectionReports, setFilteredWorkOrderInspectionReports] = useState<WorkOrderInspectionReport[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    const filteredWorkOrderInspectionReportList = data?.data?.filter((WorkOrderInspectionReport) =>
      Object.values(WorkOrderInspectionReport).some(
        (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      )
    );
    setFilteredWorkOrderInspectionReports(filteredWorkOrderInspectionReportList || []);
  }, [debouncedSearchTerm, data?.data]);

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
    const data = {
      filterJSON: JSON.stringify(gridDataState),
      columnJSON: JSON.stringify(gridColumns),
      gridName: 'WorkOrderInspectionReport',
      pageName: 'WorkOrderInspectionReport',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  const handleResetLayout = () => {
    const data = {
      filterJSON: null,
      columnJSON: null,
      gridName: 'WorkOrderInspectionReport',
      pageName: 'WorkOrderInspectionReport',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  useEffect(() => {
    if (!gridLayouts?.data) return;

    try {
      const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

      const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

      // if (filterData && columnJson) {
      setGridConfig({
        columns: columnJson,
        dataState: filterData,
      });
      // }
    } catch (error) {
      console.log('Error parsing grid layout JSON:', error);
    }
  }, [gridLayouts?.data]);

  // ----------------------------------------------------------------------------------------

  return (
    <SiTrackPage title="Work Order Inspection Report">
      <ReactHookForm formName="WORK_ORDER_INSPECTION_REPORT_FORM" onSubmit={handleSubmit} formContextProps={form}>
        <Grid container spacing={1}>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <WorkOrderInspectionFilters onShowAllWorkOrder={handleShowAll} />
          </Grid>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <Paper elevation={0} style={styling.paperStyle}>
              <SiTrackGrid
                fileTitle='work_order_inspection'
                columns={workOderInspectionReportColumns}
                dataItemKey="workOrderID"
                pdfDownload
                customCell={(field) =>
                  CustomGridCell({
                    field,
                  })
                }
                actionPermissions={{
                  export: UserPermissionName.DOWNLOAD_GRID,
                  pdfExport: UserPermissionName.DOWNLOAD_GRID_PDF,
                }}
                isBusy={isLoading || isFetching || isLoadingGridLayout}
                // handleRowClick={(data) => schedulingRedirect(data?.dataItem?.workOrderID)}
                // rowRender={rowRender}

                // -----------------Grid Layout-------
                dataItems={filteredWorkOrderInspectionReports || []}
                reorderable
                handleSearch={handleInputChange}
                handleSaveLayout={handleSaveLayout}
                isSaveLayout={true}
                gridConfigFromApi={gridConfig}
                handleResetLayout={handleResetLayout}
              // -----------------Grid Layout-------
              />
            </Paper>
          </Grid>
        </Grid>
      </ReactHookForm>
    </SiTrackPage>
  );
};
